/* eslint-disable complexity, max-lines */
import {
    BuildingOfficeIcon,
    ChevronRightIcon,
    DocumentTextIcon,
    HeroCalendarDaysIcon,
    HeroChartBarSquareIcon,
    HeroEllipsisVerticalIcon,
    HeroHomeIcon,
    NinchatQueueIcon,
} from 'components/Icon'
import {
    DefaultMenubarViewProps,
    ItemDisplayType,
} from './types'
import {
    Dropdown,
    DropdownButtonNew,
    DropdownContent,
} from 'components/Dropdown'
import React, {
    useEffect,
    useRef,
} from 'react'
import {
    channelButtonWrapper,
    dropdownContentWrapper,
} from './styles'
import {
    chevronWrapper,
    defaultMenuBarContainer,
    defaultMenuBarWrapper,
} from '../styles/Menubar'
import {
    useAppSelector,
    useVisibilityObserver,
} from 'hooks'
import {
    useChannels,
    useQueues,
    useRealm,
} from 'hooks/ninchat'
import Button from 'components/Button'
import {DefaultMenuBarChannelSettings} from './DefaultMenubarChannelSettings'
import {DefaultMenuBarOrganizationDashboard} from './DefaultMenubarOrganizationDashboard'
import {DefaultMenuBarQueueSettings} from './DefaultMenubarQueueSettings'
import {DefaultMenuBarSettings} from './DefaultMenubarSettings'
import {Icon} from 'components/Icon/types'
import LetterAvatar from 'components/Avatar/LetterAvatar'
import {RouteParamsList} from 'navigation/types'
import {navigation} from 'navigation'
import {renderToStaticMarkup} from 'react-dom/server'
import {styled} from 'stitches/config'
import {useTranslation} from 'react-i18next'

const StyledDefaultMenuBarContainer = styled('div', defaultMenuBarContainer)
const StyledDefaultMenuBarWrapper = styled('div', defaultMenuBarWrapper)
const StyledChevronWrapper = styled('div', chevronWrapper)
const StyledChannelButtonWrapper = styled('div', channelButtonWrapper)
const StyledDropdownContentWrapper = styled('div', dropdownContentWrapper)

const DefaultMenubarView = (props: DefaultMenubarViewProps): React.ReactElement => {
    const {
        name, routeParams,
    } = props

    const {t} = useTranslation()
    const menuBarRef = useRef<HTMLDivElement>(null)

    const menuBar = useVisibilityObserver()

    const {
        audience_id, queue_id, channel_id,
    } = useAppSelector(({navigation: navigationState}) => navigation.route.params<any>(navigationState.routes[navigationState.position])) || {}

    const selectedRealmId = useAppSelector(({ui}) => ui.realm_id) || ''

    const realm = useRealm(selectedRealmId, true)

    const realmName = realm?.realm_attrs.name

    const currentChannel = useChannels([channel_id])[channel_id]
    const channelName = currentChannel?.channel_attrs?.name

    const channels = useAppSelector(({ninchat}) => ninchat.user_channels)
    const queueId = Object.values(channels).find((channel) => channel?.channel_attrs?.audience_id === audience_id)?.channel_attrs?.queue_id || queue_id
    const queueName = useQueues([queueId], false)?.[queueId]?.queue_attrs?.name

    const showProfileSettings = name === 'Settings'
    const showQueueSettings = name === 'QueueSettings'
    const showChannelSettings = name === 'ChannelSettings'
    const showOrganizationSettings = name === 'Realm'
    const showQueue = name === 'Queue'
    const showScheduledAppointmentsItem = name === 'ScheduledAppointments'
    const showQueueStatisticsItem = name === 'QueueStatistics'
    const showTranscriptItem = name === 'Transcript'

    useEffect(() => {
        menuBar.setRef(menuBarRef.current as Element)
    }, [])

    const renderSheduleAppoitments = () => (
        <>
            <StyledChevronWrapper><ChevronRightIcon /></StyledChevronWrapper>
            <Button
                color='text'
                iconLeft={HeroCalendarDaysIcon as unknown as Icon}
                onPress={() => navigation.push('ScheduledAppointments')}
                text={t('Scheduled appointments')}
            />
        </>
    )

    const renderQueueStatistics = () => {
        const params = routeParams as RouteParamsList['QueueStatistics']

        return (
            <>
                <StyledChevronWrapper><ChevronRightIcon /></StyledChevronWrapper>

                {params.tab === 'metadata' ?
                    <Button
                        color='text'
                        iconLeft={DocumentTextIcon as unknown as Icon}
                        onPress={() => navigation.push('QueueStatistics', {
                            ...params,
                            queue_id: queueId,
                            tab: 'metadata',
                        })}
                        text={t('Metadata & questionnaire answers')}
                    />
                    :
                    <Button
                        color='text'
                        iconLeft={HeroChartBarSquareIcon as unknown as Icon}
                        onPress={() => navigation.push('QueueStatistics', {
                            ...params,
                            queue_id: queueId,
                            tab: 'default',
                        })}
                        text={t('Queue statistics')}
                    />}
            </>
        )
    }

    const renderTranscript = () => (
        <>
            <StyledChevronWrapper><ChevronRightIcon /></StyledChevronWrapper>
            <Button
                color='text'
                iconLeft={DocumentTextIcon}
                onPress={() => navigation.push('Transcript', {audience_id})}
                text={t('Transcript')}
            />
        </>
    )

    const renderRealmName = (insideDropdown?: boolean) => (
        <>
            {!insideDropdown && <StyledChevronWrapper><ChevronRightIcon /></StyledChevronWrapper>}
            <Button
                color='text'
                iconLeft={BuildingOfficeIcon}
                onPress={() => navigation.push('Realm', {realm_id: selectedRealmId})}
                text={realmName}
            />
        </>
    )

    const renderQueueName = (insideDropdown?: boolean) => (
        <>
            {!insideDropdown && <StyledChevronWrapper><ChevronRightIcon /></StyledChevronWrapper>}
            <Button
                color='text'
                iconLeft={NinchatQueueIcon}
                onPress={() => navigation.push('Queue', {queue_id: queueId})}
                text={queueName}
            />
        </>
    )

    const renderChannelName = (insideDropdown?: boolean) => (
        <>
            {!insideDropdown && <StyledChevronWrapper><ChevronRightIcon /></StyledChevronWrapper>}
            <Button
                color='text'
                onPress={() => navigation.push('Channel', {channel_id})}
                type='button'
            >
                <StyledChannelButtonWrapper>
                    <LetterAvatar
                        name={channelName}
                        size='small'
                    />
                    <div>
                        {channelName}
                    </div>
                </StyledChannelButtonWrapper>
            </Button>
        </>)

    const renderMobileDropdownContent = () => (
        <>
            {/* render realm name */}
            {/* eslint-disable-next-line no-extra-parens */}
            {(showQueueSettings || showChannelSettings || showQueueStatisticsItem || showTranscriptItem || showQueue || (showOrganizationSettings &&
                // eslint-disable-next-line no-extra-parens
                (routeParams as RouteParamsList['Realm'])?.tab)) ?
                renderRealmName(true)
                : null}

            {/* render queue name */}
            {/* eslint-disable-next-line no-extra-parens */}
            {(showQueueSettings || showQueueStatisticsItem || showTranscriptItem) ? renderQueueName(true) : null}

            {/* render channel name */}
            {showChannelSettings ? renderChannelName(true) : null}

            {/* render profile settings */}
            {showProfileSettings ? <DefaultMenuBarSettings params={routeParams as RouteParamsList['Settings']} type={ItemDisplayType.middleItems} /> : null}

            {/* render queue settings (schedule, advenced) */}
            {showQueueSettings ? <DefaultMenuBarQueueSettings params={routeParams as RouteParamsList['QueueSettings']} queueId={queueId} type={ItemDisplayType.middleItems} /> : null}

            {/* render channel settings (privacy, schedule) */}
            {showChannelSettings ? <DefaultMenuBarChannelSettings channelId={channel_id} params={routeParams as RouteParamsList['ChannelSettings']} type={ItemDisplayType.middleItems} /> : null}
        </>
    )

    const renderMobileOutsideDropdownContent = () => (
        <>
            { /* render realm name   */ }
            { /* eslint-disable-next-line no-extra-parens */ }
            { showOrganizationSettings && !(routeParams as RouteParamsList['Realm'])?.tab ? renderRealmName() : null }

            { /* render queue name */ }
            { showQueue ? renderQueueName() : null }

            { /* render profile settings */ }
            { showProfileSettings ? <DefaultMenuBarSettings params={routeParams as RouteParamsList['Settings']} type={ItemDisplayType.lastItem} /> : null }

            { /* render queue settings (schedule, advenced) */ }
            { showQueueSettings ? <DefaultMenuBarQueueSettings params={routeParams as RouteParamsList['QueueSettings']} queueId={queueId} type={ItemDisplayType.lastItem} /> : null }

            { /* render channel settings (privacy, schedule) */ }
            { showChannelSettings ?
                <DefaultMenuBarChannelSettings
                    channelId={channel_id}
                    params={routeParams as RouteParamsList['ChannelSettings']}
                    type={ItemDisplayType.lastItem}
                />
                : null }

            { /* render organization settings (members, queues, sites) */ }
            { showOrganizationSettings ? <DefaultMenuBarOrganizationDashboard params={routeParams as RouteParamsList['Realm']} realmId={selectedRealmId} /> : null }

            { /* render schedule appointment item */ }
            { showScheduledAppointmentsItem ? renderSheduleAppoitments() : null }

            { /* render queue statistics item */ }
            { showQueueStatisticsItem ? renderQueueStatistics() : null }

            { /* render transcript item */ }
            { showTranscriptItem ? renderTranscript() : null }
        </>)


    const showMobileMenu = !menuBar.isVisible
    const mobileDropdownContent = renderMobileDropdownContent()
    const mobileOutsideDropdown = renderMobileOutsideDropdownContent()

    return (
        <StyledDefaultMenuBarContainer>
            {showMobileMenu ?
                (
                    <StyledDefaultMenuBarWrapper>
                        <Button
                            color='text'
                            iconLeft={HeroHomeIcon as unknown as Icon}
                            onPress={() => navigation.push('Home')}
                        />
                        <>
                            {/* render dropdown only if components render shomething */}
                            {renderToStaticMarkup(mobileDropdownContent) ?
                                (
                                    <>
                                        <StyledChevronWrapper><ChevronRightIcon /></StyledChevronWrapper>
                                        <Dropdown>
                                            <DropdownButtonNew caret={false} color='text' iconRight={HeroEllipsisVerticalIcon as unknown as Icon} />
                                            <DropdownContent direction='left' >
                                                <StyledDropdownContentWrapper>
                                                    {mobileDropdownContent}
                                                </StyledDropdownContentWrapper>
                                            </DropdownContent>
                                        </Dropdown>
                                    </>
                                )
                                : null}
                            {mobileOutsideDropdown}
                        </>
                    </StyledDefaultMenuBarWrapper>
                )
                : null}

            <StyledDefaultMenuBarWrapper hidden={showMobileMenu} ref={menuBarRef}>
                <Button
                    color='text'
                    iconLeft={HeroHomeIcon as unknown as Icon}
                    onPress={() => navigation.push('Home')}
                />
                <>
                    {/* render realm name */}
                    {/* eslint-disable-next-line no-extra-parens */}
                    {(showQueueSettings || showChannelSettings || showOrganizationSettings || showQueueStatisticsItem || showTranscriptItem || showQueue) ? renderRealmName() : null}

                    {/* render queue name */}
                    {/* eslint-disable-next-line no-extra-parens */}
                    {(showQueue || showQueueSettings || showQueueStatisticsItem || showTranscriptItem) ? renderQueueName() : null}

                    {/* render channel name */}
                    {showChannelSettings ? renderChannelName() : null}

                    {/* render profile settings */}
                    {showProfileSettings ? <DefaultMenuBarSettings params={routeParams as RouteParamsList['Settings']} type={ItemDisplayType.all} /> : null}

                    {/* render queue settings (schedule, advenced) */}
                    {showQueueSettings ? <DefaultMenuBarQueueSettings params={routeParams as RouteParamsList['QueueSettings']} queueId={queueId} type={ItemDisplayType.all} /> : null}

                    {/* render channel settings (privacy, schedule) */}
                    {showChannelSettings ? <DefaultMenuBarChannelSettings channelId={channel_id} params={routeParams as RouteParamsList['ChannelSettings']} type={ItemDisplayType.all} /> : null}

                    {/* render organization settings (members, queues, sites) */}
                    {showOrganizationSettings ? <DefaultMenuBarOrganizationDashboard params={routeParams as RouteParamsList['Realm']} realmId={selectedRealmId} /> : null}

                    {/* render schedule appointment item */}
                    {showScheduledAppointmentsItem ? renderSheduleAppoitments() : null}

                    {/* render queue statistics item */}
                    {showQueueStatisticsItem ? renderQueueStatistics() : null}

                    {/* render transcript item */}
                    {showTranscriptItem ? renderTranscript() : null}
                </>
            </StyledDefaultMenuBarWrapper>
        </StyledDefaultMenuBarContainer>
    )
}

export default DefaultMenubarView
